import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps, useWindowDimensions } from 'react-native'
import {
  ApiError,
  DataWithUpload,
  Draft,
  FloorplanData,
  useFloorMutation,
  useFloorplanMutation,
  useStore,
} from 'zens-store'
import { Suspense, useState } from 'react'
import { useRouter, useNumberParams } from 'zens-router'
import { NavigationDelete, FloorplanEditor, FloorplanEditorStep, LayoutHeader, FloorplanHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
})

interface Props extends ViewProps {}

const FloorSettingsScreen = (props: Props) => {
  const [confirmDelete, setConfirmDelete] = useState<Draft<FloorplanData>>()
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const { message } = useStore()
  const { t } = useTranslation()
  const { style, ...otherProps } = props
  const { height } = useWindowDimensions()
  const contentStyle = { minHeight: height }

  const [{ companyId, locationId, buildingId, floorId }] = useNumberParams()
  const floorplanMutation = useFloorplanMutation({ companyId })
  const floorMutation = useFloorMutation({ companyId })

  async function save(draft: Draft<DataWithUpload<FloorplanData>>) {
    try {
      setErrors({})
      const { upload, ...otherData } = draft.data
      if (upload || upload === null) {
        try {
          const newFloorplan = await floorplanMutation.upload(draft, upload)
          draft.update('floorplan.background', newFloorplan?.background)
        } catch (error) {
          const { message: errorMessage } = error as ApiError
          console.debug(`FLOORPLAN: Floorplan upload failed: ${errorMessage}`)
          message.add({
            message: `${t('Floor could not be saved')}. ${errorMessage}`,
            status: 'error',
          })
          draft.update('progress', undefined)
          return
        }
      }

      await floorplanMutation.update(new Draft(otherData))
      draft.settle()
      message.add({
        message: t('Floorplan was updated successfully'),
        status: 'success',
      })
      router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/${floorId}/edit`)
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`FLOORPLAN: Floor save failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Floorplan could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
    }
  }

  async function cancel() {
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/${floorId}/edit`)
  }

  async function remove() {
    try {
      await floorMutation.delete(confirmDelete!)
      message.add({
        message: t('Floor was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`FLOORPLAN: Floor deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Floor could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }
    router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
  }

  return (
    <View style={[styles.container, contentStyle, style]} {...otherProps}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <FloorplanHeader
          companyId={companyId}
          locationId={locationId}
          buildingId={buildingId}
          floorId={floorId}
          step={FloorplanEditorStep.Settings}
        />
      </Suspense>
      <Suspense fallback={<FloorplanEditor.Skeleton style={styles.content} step={FloorplanEditorStep.Settings} />}>
        <FloorplanEditor
          style={styles.content}
          companyId={companyId}
          buildingId={buildingId}
          floorId={floorId}
          step={FloorplanEditorStep.Settings}
          errors={errors}
          onSaveDraft={save}
          onCancel={cancel}
          onDelete={async (draft) => setConfirmDelete(draft)}
        />
      </Suspense>
      <NavigationDelete
        entity={t('Floor')}
        visible={!!confirmDelete}
        onClose={() => setConfirmDelete(undefined)}
        onDelete={remove}
      />
    </View>
  )
}

export default FloorSettingsScreen
